import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SfmcMobilePush } from 'capacitor-sfmc-mobile-push';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { resolveContactKey } from '@/lib/core/hooks/useSfmcContactKeySync';
import {
  PUSH_NOTIFICATIONS_CONSENTED_KEY,
  PUSH_NOTIFICATIONS_OPT_OUT_KEY,
} from '@/lib/notifications/pushNotifications';

/**
 * On-device SFMC diagnostics — reachable via the com.jncquoi.app:///debug/sfmc deep link.
 * Added because a real TestFlight device couldn't be paired with Xcode for console access
 * during a live debugging session; this surfaces the same information directly on the phone.
 * Not gated behind login/onboarding on purpose — needs to be checkable from any app state.
 */
export function DebugSfmcPage() {
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const [diagnostics, setDiagnostics] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      // ?forceReenroll=true — SFMC's enablePush() appears to no-op server-side when the SDK's
      // own local state already reports push as enabled (observed: re-running login/setContactKey
      // did not clear a stale device-level opt-out in Contact Builder). Forcing disablePush()
      // then enablePush() guarantees a real state transition, so the SDK actually re-sends the
      // opt-in to Marketing Cloud instead of treating the call as redundant.
      let forceReenrollResult: string | undefined;
      if (Capacitor.isNativePlatform() && new URLSearchParams(location.search).get('forceReenroll') === 'true') {
        try {
          // requestPermissions() shows the real OS system dialog — this awaits until a person
          // taps Allow/Don't Allow on it. Needed because enablePush() alone can't produce a real
          // device token (and SFMC can't confirm opt-in) without OS authorization granted first.
          const permResult = await SfmcMobilePush.requestPermissions();
          await SfmcMobilePush.disablePush();
          await new Promise(resolve => setTimeout(resolve, 1000));
          await SfmcMobilePush.enablePush();
          forceReenrollResult = `requestPermissions=${permResult.receive}; disablePush() then enablePush() completed`;
        } catch (err) {
          forceReenrollResult = `failed: ${String(err)}`;
        }
      }
      // authUserContactId is the raw dev-database value — misleading on its own, since
      // PROD_CONTACT_KEY_OVERRIDES may substitute a different, production Salesforce Contact ID
      // for known QA accounts. effectiveContactKey/usedOverride show what useSfmcContactKeySync
      // actually sends natively, using the exact same resolution logic (not a duplicate).
      const { contactKey: effectiveContactKey, usedOverride } = resolveContactKey(
        user?.contactId,
        user?.email
      );
      const localState = {
        isNativePlatform: Capacitor.isNativePlatform(),
        platform: Capacitor.getPlatform(),
        [PUSH_NOTIFICATIONS_CONSENTED_KEY]: localStorage.getItem(PUSH_NOTIFICATIONS_CONSENTED_KEY),
        [PUSH_NOTIFICATIONS_OPT_OUT_KEY]: localStorage.getItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY),
        sfmc_anonymous_contact_key: localStorage.getItem('sfmc_anonymous_contact_key'),
        authUserContactId: user?.contactId ?? null,
        authUserEmail: user?.email ?? null,
        effectiveContactKey,
        usedProdContactKeyOverride: usedOverride,
      };

      if (!Capacitor.isNativePlatform()) {
        setDiagnostics({ localState, native: 'skipped (web platform)' });
        return;
      }

      // ?dumpInbox=true — summarized getInboxMessages() output (title/url/customKeys per
      // message), to check a real SFMC Inbox send's deep-link data on-device.
      let inboxSummary: unknown;
      if (Capacitor.isNativePlatform() && new URLSearchParams(location.search).get('dumpInbox') === 'true') {
        try {
          const { messages } = await SfmcMobilePush.getInboxMessages();
          inboxSummary = messages.map(m => ({ title: m.title, url: m.url, customKeys: m.customKeys }));
        } catch (err) {
          inboxSummary = `failed: ${String(err)}`;
        }
      }

      try {
        const [permissions, native] = await Promise.all([
          SfmcMobilePush.checkPermissions(),
          SfmcMobilePush.getDiagnostics(),
        ]);
        setDiagnostics({ inboxSummary, localState, osPermission: permissions, native, forceReenrollResult });
      } catch (err) {
        setError(String(err));
        setDiagnostics({ inboxSummary, localState, forceReenrollResult });
      }
    }

    load();
  }, [user?.contactId, user?.email]);

  return (
    <pre
      style={{
        padding: 16,
        fontSize: 11,
        background: '#000',
        color: '#0f0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        overflowY: 'auto',
        height: '100vh',
        margin: 0,
      }}
    >
      {diagnostics ? JSON.stringify(diagnostics, null, 2) : 'Loading…'}
      {error ? `\n\nERROR: ${error}` : ''}
    </pre>
  );
}
